// Stage Mail-V2 Stage 4 (2026-07-22): useDrafts hook
//
// 拉 Drafts folder 列表(走 mail_drafts 表,不是 mail_messages.mailbox_name='DRAFTS')。
// 与 useMailMessages 并列,但 shape 不同(走 MailDraftDTO,前端 adapter 把它
// 适配成 MessageV2View 让 MailV2List 复用)。
//
// 模式跟 useMailMessages 一致:in-flight cancel + refresh。

import { useCallback, useEffect, useState } from "react";
import { mailV2Api, type MailDraftDTO } from "../../../api/mail-v2";

interface UseDraftsArgs {
  enabled: boolean;
  mailAccountId: string | null;
}

interface UseDraftsReturn {
  items: MailDraftDTO[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useDrafts({ enabled, mailAccountId }: UseDraftsArgs): UseDraftsReturn {
  const [items, setItems] = useState<MailDraftDTO[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState<number>(0);

  const refresh = useCallback(() => {
    setRefreshTick((t) => t + 1);
  }, []);

  useEffect(() => {
    if (!enabled) {
      // Reset to empty when not enabled (matches useMailMessages shim behavior).
      setItems([]);
      setTotal(0);
      setLoading(false);
      setError(null);
      return;
    }
    if (!mailAccountId) {
      setItems([]);
      setTotal(0);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const rows = await mailV2Api.listDraftsByFolder({
          mail_account_id: mailAccountId,
          smtp_status: "draft,sending",
        });
        if (cancelled) return;
        setItems(rows);
        setTotal((rows ?? []).length);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setItems([]);
        setTotal(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, mailAccountId, refreshTick]);

  return { items, total, loading, error, refresh };
}